/**
 * A small choice over a screen: from the bottom on a phone, a box from sm up.
 *
 * On a phone it rises from the bottom edge, under the thumb, with the screen
 * it belongs to still in sight above it. On a desk the same content is a small
 * box in the middle. Used for picking one thing out of a few — a stage, a
 * date, a plant — and for asking before something is gone.
 *
 * Used by: the sheets across the plant card, beds, packets, the photo tray,
 * the profile, Today's to-dos and the gardener's own to-dos (D50: its box
 * takes the focus as the sheet opens, initialFocus).
 *
 * @module BottomPanel
 */

import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

/** The dialog box made a bottom sheet below sm: pinned to the foot, rounded on top, rising in. */
const BOTTOM_PANEL_CLASS = cn(
  'sm:max-w-sm',
  'max-sm:bottom-0 max-sm:left-0 max-sm:top-auto max-sm:max-h-[85dvh] max-sm:translate-x-0 max-sm:rounded-t-xl max-sm:border-x-0 max-sm:border-b-0 max-sm:px-4 max-sm:pb-[max(1rem,env(safe-area-inset-bottom))] max-sm:pt-5',
  'max-sm:data-[state=open]:slide-in-from-bottom-8 max-sm:data-[state=open]:slide-in-from-left-0 max-sm:data-[state=open]:zoom-in-100',
);

/**
 * A titled panel: a sheet from the bottom on a phone, a small dialog from sm up.
 *
 * @param props.open - Whether it shows.
 * @param props.onOpenChange - Told when it opens or closes (×, a tap outside, Escape, a swipe).
 * @param props.title - Its heading, which also names it for screen readers.
 * @param props.children - What it holds.
 * @param props.className - More classes for the panel.
 * @param props.wrapTitle - A title that must be read whole wraps instead of cutting off.
 * @param props.initialFocus - What takes the focus as it opens (a text box, so the keyboard comes up); else Radix's first.
 */
export const BottomPanel: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: React.ReactNode;
  className?: string;
  /** A title that is a question, which must be read whole: wraps instead of cutting off. */
  wrapTitle?: boolean;
  /** What takes the focus as it opens (the own to-do sheet's box, D50); Radix's first focusable when absent. */
  initialFocus?: React.RefObject<HTMLElement>;
}> = ({ open, onOpenChange, title, children, className, wrapTitle = false, initialFocus }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent
      className={cn(BOTTOM_PANEL_CLASS, className)}
      aria-describedby={undefined}
      onOpenAutoFocus={initialFocus ? (event) => {
        if (!initialFocus.current) return;
        event.preventDefault();
        initialFocus.current.focus();
      } : undefined}
    >
      <DialogHeader className="text-left">
        <DialogTitle className={cn('text-lg', wrapTitle ? 'pr-6 leading-snug' : 'truncate')}>{title}</DialogTitle>
      </DialogHeader>
      {children}
    </DialogContent>
  </Dialog>
);

/**
 * One choice in a panel's list, ticked when it is the current one.
 *
 * @param props.selected - The current choice: drawn bold on a tint.
 * @param props.onClick - Picks it.
 * @param props.children - Its words (and an icon).
 */
export const PanelOption: React.FC<{
  selected?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ selected, onClick, children }) => (
  <button
    type="button"
    role="option"
    aria-selected={Boolean(selected)}
    onClick={onClick}
    className={cn(
      '-mx-2 flex min-h-11 w-[calc(100%+1rem)] items-center gap-2.5 rounded-md px-2 text-left text-[0.9375rem]',
      'hover:bg-accent focus-visible:bg-accent focus-visible:outline-none',
      selected && 'bg-primary/10 font-semibold',
    )}
  >
    {children}
  </button>
);
